import React, { useState, useEffect } from 'react';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  Box,
  Grid,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Phone as PhoneIcon,
  Person as PersonIcon,
  AccessTime as AccessTimeIcon,
  AttachMoney as MoneyIcon,
  Info as InfoIcon,
  Timeline as TimelineIcon
} from '@mui/icons-material';
import AudioPlayer from '../components/AudioPlayer';
import { useParams, useNavigate } from 'react-router-dom';
import { callService } from '../services/api';

const CallDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [call, setCall] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchCallDetails = async () => {
      try {
        setLoading(true);
        console.log('🔍 Fetching call details for ID:', id);
        const response = await callService.getCallById(id);
        console.log('📦 Call details response:', response);
        // Handle both direct response and wrapped response formats
        const callData = response.data || response;
        console.log('📋 Processed call data:', callData);
        setCall(callData);
      } catch (err) {
        console.error('❌ Error fetching call details:', err);
        setError('Failed to load call details');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchCallDetails();
    }
  }, [id]);

  // Helper function to get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'failed':
        return 'error';
      case 'answered':
        return 'info';
      case 'ringing':
        return 'warning';
      case 'initiated':
        return 'primary';
      default:
        return 'default';
    }
  };

  // Helper function to format duration
  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Helper function to format cost
  const formatCost = (cost) => {
    if (!cost) return 'N/A';
    return `$${parseFloat(cost).toFixed(4)}`;
  };

  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  // Helper function to get phone numbers
  const getPhoneNumbers = (call) => {
    if (call.metadata && call.metadata.fromNumber && call.metadata.toNumber) {
      return {
        fromNumber: call.metadata.fromNumber,
        toNumber: call.metadata.toNumber
      };
    }
    return {
      fromNumber: call.phoneNumber || 'N/A',
      toNumber: call.phoneNumber || 'N/A'
    };
  };

  // Helper function to get agent name
  const getAgentName = (call) => {
    if (call.agentId && typeof call.agentId === 'object' && call.agentId.name) {
      return call.agentId.name;
    }
    if (call.agentName) {
      return call.agentName;
    }
    return 'Unknown Agent';
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/calls')}
        >
          Back to Calls
        </Button>
      </Container>
    );
  }

  if (!call) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="warning" sx={{ mb: 3 }}>
          Call not found
        </Alert>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/calls')}
        >
          Back to Calls
        </Button>
      </Container>
    );
  }

  const phoneNumbers = getPhoneNumbers(call);
  const agentName = getAgentName(call);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/calls')}
          >
            Back to Calls
          </Button>
          <Typography variant="h4" component="h1">
            Call Details
          </Typography>
        </Box>
        <Chip
          label={call.status}
          color={getStatusColor(call.status)}
          size="large"
        />
      </Box>

      <Grid container spacing={3}>
        {/* Main Call Information */}
        <Grid item xs={12} md={8}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Call Information
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <PersonIcon fontSize="small" />
                    <Typography variant="body2" color="text.secondary">
                      Agent:
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {agentName}
                    </Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <PhoneIcon fontSize="small" />
                    <Typography variant="body2" color="text.secondary">
                      From:
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {phoneNumbers.fromNumber}
                    </Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <PhoneIcon fontSize="small" />
                    <Typography variant="body2" color="text.secondary">
                      To:
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {phoneNumbers.toNumber}
                    </Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <AccessTimeIcon fontSize="small" />
                    <Typography variant="body2" color="text.secondary">
                      Duration:
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {formatDuration(call.duration)}
                    </Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <MoneyIcon fontSize="small" />
                    <Typography variant="body2" color="text.secondary">
                      Cost:
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {formatCost(call.cost)}
                    </Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <TimelineIcon fontSize="small" />
                    <Typography variant="body2" color="text.secondary">
                      Direction:
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {call.metadata?.direction || 'N/A'}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Call Timeline */}
        <Grid item xs={12} md={4}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Call Timeline
              </Typography>
              
              <Box>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <InfoIcon fontSize="small" color="primary" />
                  <Typography variant="body2" color="text.secondary">
                    Created:
                  </Typography>
                  <Typography variant="body2">
                    {formatDate(call.createdAt)}
                  </Typography>
                </Box>
                
                {call.startTime && (
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <AccessTimeIcon fontSize="small" color="info" />
                    <Typography variant="body2" color="text.secondary">
                      Started:
                    </Typography>
                    <Typography variant="body2">
                      {formatDate(call.startTime)}
                    </Typography>
                  </Box>
                )}
                
                {call.endTime && (
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <AccessTimeIcon fontSize="small" color="success" />
                    <Typography variant="body2" color="text.secondary">
                      Ended:
                    </Typography>
                    <Typography variant="body2">
                      {formatDate(call.endTime)}
                    </Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Recording */}
        {call.recordingUrl && (
          <Grid item xs={12}>
            <Card elevation={2}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Call Recording
                </Typography>
                <AudioPlayer
                  audioUrl={call.recordingUrl}
                  title="Call Recording"
                  height={80}
                  showTitle={false}
                />
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Transcript */}
        {call.transcript && (
          <Grid item xs={12}>
            <Card elevation={2}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Call Transcript
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, maxHeight: 300, overflow: 'auto' }}>
                  <Typography variant="body2" style={{ whiteSpace: 'pre-wrap' }}>
                    {call.transcript}
                  </Typography>
                </Paper>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Metadata */}
        {call.metadata && Object.keys(call.metadata).length > 0 && (
          <Grid item xs={12}>
            <Card elevation={2}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Call Metadata
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Key</TableCell>
                        <TableCell>Value</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Object.entries(call.metadata).map(([key, value]) => (
                        <TableRow key={key}>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {key}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Container>
  );
};

export default CallDetailsPage; 